// src/belediye/redux/slices/belediyeDashboardV2Slice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchDashboardQuery,
  fetchDashboardQueryQuiet,
  fetchDashboardWidgetData,
  fetchBm4BoundaryDefaultConfig,
  fetchBm4Boundaries,
  fetchBm4BoundaryDetail,
  fetchBm4MahalleStats,
} from "../../services/dashboardApi";

/* ============================================================================
   Dashboard Slice
   - Summary: GET /dashboard/query/
   - Detail : GET /dashboard/widget-data/
   - BM4    : /bm4/boundaries, /bm4/boundaries/default, /bm4/mahalle-stats
   - Debug  : localStorage.DEBUG_DASH_V2 = "1"
============================================================================ */

const isBrowser = typeof window !== "undefined";

const safeDebugFlag = () => {
  if (!isBrowser) return false;
  try {
    return (
      window.localStorage.getItem("DEBUG_DASH_V2") === "1" ||
      window.__DEBUG_DASH_V2__ === true
    );
  } catch {
    return false;
  }
};

const DEBUG = safeDebugFlag();

const log = (...args) =>
  DEBUG && console.log("[belediyeDashboardV2Slice]", ...args);
const warn = (...args) =>
  DEBUG && console.warn("[belediyeDashboardV2Slice]", ...args);
const errlog = (...args) =>
  DEBUG && console.error("[belediyeDashboardV2Slice]", ...args);

const nowIso = () => new Date().toISOString();

const STATUS_ALIASES = Object.freeze({
  all: "tümü",
  tumu: "tümü",
  tümü: "tümü",
  hepsi: "tümü",

  new: "yeni",
  yeni: "yeni",

  in_review: "işleme_alındı",
  review: "işleme_alındı",
  "işleme_alındı": "işleme_alındı",
  "işleme alındı": "işleme_alındı",
  "incelemede": "işleme_alındı",
  "incelemeye alındı": "işleme_alındı",

  solved: "çözüldü",
  "çözüldü": "çözüldü",

  unsolved: "çözülemedi",
  "çözülemedi": "çözülemedi",
});

const STATUS_ORDER = ["yeni", "işleme_alındı", "çözüldü", "çözülemedi", "tümü"];

const WIDGET_KEYS = Object.freeze([
  "latest_new",
  "waiting_new",
  "neighborhood_breakdown",
  "unit_breakdown",
  "open_age_distribution",
]);

const BM4_LEVELS = Object.freeze(["il", "ilce", "mahalle"]);

const ilkFiltreler = {
  date_from: undefined,
  date_to: undefined,
  unit_id: undefined,
  statuses: ["yeni", "işleme_alındı"],
  map_points_limit: 5000,
};

const ilkBm4BoundaryListByLevel = () => ({
  il: null,
  ilce: null,
  mahalle: null,
});

const ilkBm4State = () => ({
  defaultConfig: null,
  defaultConfigMeta: null,
  boundaryListByLevel: ilkBm4BoundaryListByLevel(),
  boundaryYukleniyorByLevel: {},
  boundaryDetailById: {},
  boundaryDetailYukleniyorById: {},
  mahalleStats: null,
  mahalleStatsYukleniyor: false,

  selectedLayerLevel: null,
  selectedBoundaryId: null,
  selectedMahalleId: null,

  mapView: {
    center: null, // { lat, lng }
    zoom: null,
    source: null, // default | boundary | user
  },

  hata: null,
});

/* ----------------------------------------------------------------------------
   Helpers
---------------------------------------------------------------------------- */

const tekilSirali = (arr) => {
  const seen = new Set();
  const out = [];
  for (const item of arr || []) {
    if (seen.has(item)) continue;
    seen.add(item);
    out.push(item);
  }
  return out;
};

const statusNormalizeEt = (raw) => {
  const token = String(raw ?? "").trim();
  if (!token) return "";
  const lower = token.toLowerCase();
  return STATUS_ALIASES[lower] || token;
};

const siraliStatusListesi = (list) => {
  const uniq = tekilSirali(list);
  return [...uniq].sort((a, b) => {
    const ai = STATUS_ORDER.indexOf(a);
    const bi = STATUS_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return String(a).localeCompare(String(b), "tr");
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
};

const widgetKeyNormalizeEt = (raw) => {
  const key = String(raw ?? "").trim();
  return WIDGET_KEYS.includes(key) ? key : undefined;
};

const bm4LevelNormalizeEt = (raw) => {
  const level = String(raw ?? "").trim().toLowerCase();
  return BM4_LEVELS.includes(level) ? level : undefined;
};

const sayiNormalizeEt = (raw) => {
  if (raw === undefined || raw === null || raw === "") return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
};

const latLngNormalizeEt = (raw) => {
  if (!raw) return null;

  if (Array.isArray(raw) && raw.length >= 2) {
    const lat = Number(raw[0]);
    const lng = Number(raw[1]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng };
    }
    return null;
  }

  if (typeof raw === "object") {
    const lat = Number(
      raw.lat ?? raw.latitude ?? raw.center_lat ?? raw.enlem
    );
    const lng = Number(
      raw.lng ?? raw.longitude ?? raw.center_lng ?? raw.boylam ?? raw.lon
    );

    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng };
    }
  }

  return null;
};

const zoomNormalizeEt = (raw) => {
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const bm4ViewFromPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return { center: null, zoom: null };
  }

  const candidate =
    payload.default ||
    payload.data ||
    payload;

  return {
    center: latLngNormalizeEt(
      candidate.center || {
        center_lat: candidate.center_lat,
        center_lng: candidate.center_lng,
      }
    ),
    zoom: zoomNormalizeEt(candidate.zoom ?? candidate.zoom_level),
  };
};

const dashboardFiltrelerindenBm4StatsParametresiHazirla = (
  filtreler = {},
  override = {}
) => {
  const merged = {
    date_from: filtreler?.date_from,
    date_to: filtreler?.date_to,
    unit_id: filtreler?.unit_id,
    statuses: filtreler?.statuses,
    ...(override || {}),
  };

  if (typeof merged.statuses === "string") {
    merged.statuses = merged.statuses
      .split(",")
      .map((x) => String(x).trim())
      .filter(Boolean);
  }

  if (Array.isArray(merged.statuses)) {
    const normalized = siraliStatusListesi(
      merged.statuses.map(statusNormalizeEt).filter(Boolean)
    );
    merged.statuses = normalized.length ? normalized : undefined;
  }

  return merged;
};

/**
 * Backend contract:
 * - statuses ["tümü"] => filtre yok
 * - statuses boş/undefined => backend default ["yeni","işleme_alındı"]
 */
const normallestirFiltre = (filtreler) => {
  const f = { ...filtreler };

  if (typeof f.statuses === "string") {
    f.statuses = f.statuses
      .split(",")
      .map((x) => String(x).trim())
      .filter(Boolean);
  }

  if (Array.isArray(f.statuses)) {
    const normalized = siraliStatusListesi(
      f.statuses.map(statusNormalizeEt).filter(Boolean)
    );

    if (normalized.length === 0) {
      f.statuses = ["tümü"];
    } else if (normalized.includes("tümü")) {
      f.statuses = ["tümü"];
    } else {
      f.statuses = normalized;
    }
  } else if (f.statuses === "" || f.statuses === null) {
    f.statuses = undefined;
  }

  if (f.unit_id === "" || f.unit_id === null) f.unit_id = undefined;
  if (f.unit_id !== undefined) {
    const n = Number(f.unit_id);
    f.unit_id = Number.isFinite(n) ? n : undefined;
  }

  if (f.date_from === "" || f.date_from === null) f.date_from = undefined;
  if (f.date_to === "" || f.date_to === null) f.date_to = undefined;

  if (f.map_points_limit !== undefined) {
    const n = Number(f.map_points_limit);
    f.map_points_limit = Number.isFinite(n)
      ? Math.min(Math.max(n, 100), 10000)
      : 5000;
  }

  return f;
};

const widgetDetayParametresiHazirla = (payload = {}, mevcutFiltreler = {}) => {
  const {
    widget,
    limit = 50,
    offset = 0,
    expanded = true,
    ...filterOverrides
  } = payload || {};

  const normalizedFilters = normallestirFiltre({
    ...(mevcutFiltreler || ilkFiltreler),
    ...filterOverrides,
  });

  return {
    ...normalizedFilters,
    widget: widgetKeyNormalizeEt(widget),
    limit: Number.isFinite(Number(limit)) ? Number(limit) : 50,
    offset: Number.isFinite(Number(offset)) ? Number(offset) : 0,
    expanded: expanded !== false,
  };
};

const debugSummarizeFilters = (f) => ({
  date_from: f?.date_from,
  date_to: f?.date_to,
  unit_id: f?.unit_id,
  statuses: f?.statuses,
  map_points_limit: f?.map_points_limit,
});

const temizleWidgetDetayState = (state) => {
  state.widgetDetaylari = {};
  state.widgetYukleniyorByKey = {};
  state.widgetHataByKey = {};
};

const temizleBm4State = (state) => {
  state.bm4 = ilkBm4State();
};

/* ----------------------------------------------------------------------------
   Thunks — summary
---------------------------------------------------------------------------- */

export const dashboardV2SorguGetir = createAsyncThunk(
  "belediyeDashboardV2/sorguGetir",
  async (overrideParams, { getState, rejectWithValue, requestId }) => {
    const startedAt = nowIso();

    try {
      const state = getState();
      const sliceState = state?.belediyeDashboardV2;

      const birlesik = {
        ...(sliceState?.filtreler || ilkFiltreler),
        ...(overrideParams || {}),
      };

      const params = normallestirFiltre(birlesik);

      log("THUNK start", {
        requestId,
        startedAt,
        overrideParams: overrideParams || null,
        mergedFilters: debugSummarizeFilters(birlesik),
        normalizedFilters: debugSummarizeFilters(params),
      });

      if (params?.unit_id !== undefined) {
        warn(
          "THUNK: unit_id gönderilecek (başkan tümü bekliyorsa bu problem olabilir):",
          {
            requestId,
            unit_id: params.unit_id,
          }
        );
      }

      const data = await fetchDashboardQuery(params);

      const meta = data?.meta;
      const applied = data?.uygulananFiltreler;

      log("THUNK success", {
        requestId,
        finishedAt: nowIso(),
        meta,
        applied,
        kpi: data?.kpi
          ? {
              bugun: data.kpi.bugunBasvuruSayisi,
              buAy: data.kpi.buAyBasvuruSayisi,
              seciliToplam: data.kpi.seciliAralikBasvuruSayisi,
            }
          : null,
      });

      if (meta?.baskanMi && meta?.birimModu && meta.birimModu !== "tümü") {
        warn("Başkan olmasına rağmen backend birimModu tümü değil:", {
          requestId,
          birimModu: meta.birimModu,
          activeUnit: meta.aktifBirimId,
          izinliBirimCount: Array.isArray(meta.izinliBirimIdleri)
            ? meta.izinliBirimIdleri.length
            : null,
          sentUnitId: params?.unit_id,
        });
      }

      const sentUnitEmpty =
        params?.unit_id === undefined || params?.unit_id === null;

      if (
        meta?.baskanMi &&
        sentUnitEmpty &&
        (meta?.birimModu === "seçili" || meta?.birimModu === "tek")
      ) {
        warn(
          "ÇELİŞKİ: Frontend unit_id boş ama backend birimModu seçili/tek döndü.",
          {
            requestId,
            sentUnitId: params?.unit_id,
            meta,
            applied,
          }
        );
      }

      return {
        data,
        uygulananParametreler: params,
        requestId,
        startedAt,
        finishedAt: nowIso(),
      };
    } catch (err) {
      const resp = err?.response;
      const mesaj =
        resp?.data?.detail ||
        resp?.data?.message ||
        (typeof resp?.data === "string" ? resp.data : null) ||
        err?.message ||
        "Dashboard verisi alınamadı.";

      const payload = {
        mesaj,
        durum: resp?.status,
        hata: resp?.data,
        requestId,
        startedAt,
        finishedAt: nowIso(),
      };

      errlog("THUNK failed", payload);
      return rejectWithValue(payload);
    }
  }
);

export const dashboardV2SessizSorguGetir = createAsyncThunk(
  "belediyeDashboardV2/sessizSorguGetir",
  async (overrideParams, { getState, rejectWithValue, requestId }) => {
    const startedAt = nowIso();

    try {
      const state = getState();
      const sliceState = state?.belediyeDashboardV2;

      const birlesik = {
        ...(sliceState?.filtreler || ilkFiltreler),
        ...(overrideParams || {}),
      };

      const params = normallestirFiltre(birlesik);
      const data = await fetchDashboardQueryQuiet(params);

      return {
        data,
        uygulananParametreler: params,
        requestId,
        startedAt,
        finishedAt: nowIso(),
      };
    } catch (err) {
      const resp = err?.response;
      return rejectWithValue({
        mesaj:
          resp?.data?.detail ||
          resp?.data?.message ||
          err?.message ||
          "Sessiz dashboard yenilemesi başarısız.",
        durum: resp?.status,
        hata: resp?.data,
        requestId,
        startedAt,
        finishedAt: nowIso(),
      });
    }
  }
);

/* ----------------------------------------------------------------------------
   Thunks — widget detail
---------------------------------------------------------------------------- */

export const dashboardV2WidgetDetayGetir = createAsyncThunk(
  "belediyeDashboardV2/widgetDetayGetir",
  async (payload, { getState, rejectWithValue, requestId }) => {
    const startedAt = nowIso();

    try {
      const state = getState();
      const sliceState = state?.belediyeDashboardV2;
      const params = widgetDetayParametresiHazirla(
        payload,
        sliceState?.filtreler || ilkFiltreler
      );

      if (!params.widget) {
        return rejectWithValue({
          mesaj: "Widget anahtarı zorunlu.",
          requestId,
          startedAt,
          finishedAt: nowIso(),
        });
      }

      log("WIDGET THUNK start", {
        requestId,
        widget: params.widget,
        params,
      });

      const data = await fetchDashboardWidgetData(params);

      return {
        widget: params.widget,
        data,
        uygulananParametreler: params,
        requestId,
        startedAt,
        finishedAt: nowIso(),
      };
    } catch (err) {
      const resp = err?.response;
      const widget = widgetKeyNormalizeEt(payload?.widget);

      return rejectWithValue({
        widget,
        mesaj:
          resp?.data?.detail ||
          resp?.data?.message ||
          err?.message ||
          "Widget detayı alınamadı.",
        durum: resp?.status,
        hata: resp?.data,
        requestId,
        startedAt,
        finishedAt: nowIso(),
      });
    }
  }
);

/* ----------------------------------------------------------------------------
   Thunks — BM4
---------------------------------------------------------------------------- */

export const dashboardV2Bm4DefaultConfigGetir = createAsyncThunk(
  "belediyeDashboardV2/bm4DefaultConfigGetir",
  async (_payload, { rejectWithValue, requestId }) => {
    const startedAt = nowIso();

    try {
      const data = await fetchBm4BoundaryDefaultConfig();

      return {
        data,
        requestId,
        startedAt,
        finishedAt: nowIso(),
      };
    } catch (err) {
      const resp = err?.response;
      return rejectWithValue({
        mesaj:
          resp?.data?.detail ||
          resp?.data?.message ||
          err?.message ||
          "BM4 varsayılan config alınamadı.",
        durum: resp?.status,
        hata: resp?.data,
        requestId,
        startedAt,
        finishedAt: nowIso(),
      });
    }
  }
);

export const dashboardV2Bm4BoundaryListGetir = createAsyncThunk(
  "belediyeDashboardV2/bm4BoundaryListGetir",
  async (payload, { rejectWithValue, requestId }) => {
    const startedAt = nowIso();

    try {
      const params =
        typeof payload === "string"
          ? { level: payload }
          : { ...(payload || {}) };

      const level = bm4LevelNormalizeEt(params?.level);
      if (!level) {
        return rejectWithValue({
          mesaj: "Geçerli bir BM4 level zorunlu.",
          requestId,
          startedAt,
          finishedAt: nowIso(),
        });
      }

      const data = await fetchBm4Boundaries({
        with_geojson: true,
        only_active: true,
        ...params,
        level,
      });

      return {
        level,
        data,
        requestId,
        startedAt,
        finishedAt: nowIso(),
      };
    } catch (err) {
      const resp = err?.response;
      return rejectWithValue({
        level: bm4LevelNormalizeEt(payload?.level ?? payload),
        mesaj:
          resp?.data?.detail ||
          resp?.data?.message ||
          err?.message ||
          "BM4 boundary listesi alınamadı.",
        durum: resp?.status,
        hata: resp?.data,
        requestId,
        startedAt,
        finishedAt: nowIso(),
      });
    }
  }
);

export const dashboardV2Bm4BoundaryDetailGetir = createAsyncThunk(
  "belediyeDashboardV2/bm4BoundaryDetailGetir",
  async (payload, { rejectWithValue, requestId }) => {
    const startedAt = nowIso();

    try {
      const boundaryId = sayiNormalizeEt(
        payload?.boundaryId ?? payload?.id ?? payload
      );

      if (!boundaryId) {
        return rejectWithValue({
          mesaj: "Geçerli bir boundaryId zorunlu.",
          requestId,
          startedAt,
          finishedAt: nowIso(),
        });
      }

      const detailParams = {
        include_children:
          payload?.include_children ?? payload?.includeChildren ?? true,
      };

      const data = await fetchBm4BoundaryDetail(boundaryId, detailParams);

      return {
        boundaryId,
        data,
        requestId,
        startedAt,
        finishedAt: nowIso(),
      };
    } catch (err) {
      const resp = err?.response;
      const boundaryId = sayiNormalizeEt(
        payload?.boundaryId ?? payload?.id ?? payload
      );

      return rejectWithValue({
        boundaryId,
        mesaj:
          resp?.data?.detail ||
          resp?.data?.message ||
          err?.message ||
          "BM4 boundary detayı alınamadı.",
        durum: resp?.status,
        hata: resp?.data,
        requestId,
        startedAt,
        finishedAt: nowIso(),
      });
    }
  }
);

export const dashboardV2Bm4MahalleStatsGetir = createAsyncThunk(
  "belediyeDashboardV2/bm4MahalleStatsGetir",
  async (overrideParams, { getState, rejectWithValue, requestId }) => {
    const startedAt = nowIso();

    try {
      const state = getState();
      const sliceState = state?.belediyeDashboardV2;

      const params = dashboardFiltrelerindenBm4StatsParametresiHazirla(
        sliceState?.filtreler || ilkFiltreler,
        overrideParams || {}
      );

      const data = await fetchBm4MahalleStats(params);

      return {
        data,
        uygulananParametreler: params,
        requestId,
        startedAt,
        finishedAt: nowIso(),
      };
    } catch (err) {
      const resp = err?.response;
      return rejectWithValue({
        mesaj:
          resp?.data?.detail ||
          resp?.data?.message ||
          err?.message ||
          "BM4 mahalle istatistikleri alınamadı.",
        durum: resp?.status,
        hata: resp?.data,
        requestId,
        startedAt,
        finishedAt: nowIso(),
      });
    }
  }
);

/* ----------------------------------------------------------------------------
   Slice
---------------------------------------------------------------------------- */

const initialState = {
  filtreler: { ...ilkFiltreler },

  // summary payload blokları
  uygulananFiltreler: null,
  meta: null,
  kpi: null,
  grafikler: null,
  harita: null,
  kirilimlar: null,
  sonYeniBasvurular: null,
  cevapsizBasvurular: null,

  // BM4
  bm4: ilkBm4State(),

  // expand / detail
  expandAcikWidget: null,
  widgetDetaylari: {},
  widgetYukleniyorByKey: {},
  widgetHataByKey: {},

  // request state
  yukleniyor: false,
  sessizYukleniyor: false,
  hata: null,
  sonGuncellemeEpochMs: null,

  // debug / izleme
  sonIstekParametreleri: null,
  sonIstekRequestId: null,
  sonIstekZamanlari: null,
};

const belediyeDashboardV2Slice = createSlice({
  name: "belediyeDashboardV2",
  initialState,
  reducers: {
    dashboardV2FiltreleriSifirla(state) {
      log("ACTION dashboardV2FiltreleriSifirla", {
        before: debugSummarizeFilters(state.filtreler),
      });
      state.filtreler = { ...ilkFiltreler };
      temizleWidgetDetayState(state);
      log("ACTION dashboardV2FiltreleriSifirla", {
        after: debugSummarizeFilters(state.filtreler),
      });
    },

    dashboardV2FiltreleriAyarla(state, action) {
      const next = normallestirFiltre({
        ...state.filtreler,
        ...(action.payload || {}),
      });

      log("ACTION dashboardV2FiltreleriAyarla", {
        payload: action.payload || null,
        before: debugSummarizeFilters(state.filtreler),
        after: debugSummarizeFilters(next),
      });

      state.filtreler = next;
      temizleWidgetDetayState(state);
    },

    dashboardV2TarihAyarla(state, action) {
      const { date_from, date_to } = action.payload || {};

      log("ACTION dashboardV2TarihAyarla", {
        before: {
          date_from: state.filtreler.date_from,
          date_to: state.filtreler.date_to,
        },
        after: { date_from, date_to },
      });

      state.filtreler.date_from = date_from || undefined;
      state.filtreler.date_to = date_to || undefined;
      temizleWidgetDetayState(state);
    },

    dashboardV2BirimAyarla(state, action) {
      const before = state.filtreler.unit_id;
      const nextRaw = action.payload;
      const next =
        nextRaw === null || nextRaw === "" || nextRaw === undefined
          ? undefined
          : Number(nextRaw);

      log("ACTION dashboardV2BirimAyarla", { before, after: next });

      if (next !== undefined && Number.isFinite(next)) {
        state.filtreler.unit_id = next;
      } else {
        state.filtreler.unit_id = undefined;
      }

      temizleWidgetDetayState(state);
    },

    dashboardV2DurumlarAyarla(state, action) {
      const before = state.filtreler.statuses;
      const incoming = action.payload;

      const next =
        normallestirFiltre({
          statuses: incoming,
        }).statuses ?? ["yeni", "işleme_alındı"];

      log("ACTION dashboardV2DurumlarAyarla", {
        before,
        after: next,
      });

      state.filtreler.statuses = next;
      temizleWidgetDetayState(state);
    },

    dashboardV2HaritaLimitAyarla(state, action) {
      const before = state.filtreler.map_points_limit;
      const limit = Number(action.payload);
      const next = Number.isFinite(limit)
        ? Math.min(Math.max(limit, 100), 10000)
        : 5000;

      log("ACTION dashboardV2HaritaLimitAyarla", {
        before,
        after: next,
      });

      state.filtreler.map_points_limit = next;
    },

    dashboardV2Bm4KatmanAyarla(state, action) {
      const next = bm4LevelNormalizeEt(action.payload);
      if (!next) return;
      state.bm4.selectedLayerLevel = next;
    },

    dashboardV2Bm4SeciliBoundaryAyarla(state, action) {
      const payload = action.payload || {};

      const nextId = sayiNormalizeEt(payload?.id ?? payload?.boundaryId ?? payload);
      const nextLevel = bm4LevelNormalizeEt(
        payload?.level ?? payload?.selectedLayerLevel
      );
      const nextMahalleId = sayiNormalizeEt(
        payload?.mahalleId ?? payload?.neighborhoodId ?? payload?.neighborhood_id
      );
      const nextCenter = latLngNormalizeEt(payload?.center || payload);
      const nextZoom = zoomNormalizeEt(payload?.zoom ?? payload?.zoom_level);

      if (nextId !== undefined) {
        state.bm4.selectedBoundaryId = nextId;
      }

      if (nextLevel) {
        state.bm4.selectedLayerLevel = nextLevel;
      }

      if (nextMahalleId !== undefined) {
        state.bm4.selectedMahalleId = nextMahalleId;
      }

      if (nextCenter || nextZoom !== null) {
        state.bm4.mapView = {
          center: nextCenter || state.bm4.mapView.center,
          zoom: nextZoom !== null ? nextZoom : state.bm4.mapView.zoom,
          source: "user",
        };
      }
    },

    dashboardV2Bm4SeciliBoundaryTemizle(state) {
      state.bm4.selectedBoundaryId = null;
      state.bm4.selectedMahalleId = null;
    },

    dashboardV2Bm4SeciliMahalleAyarla(state, action) {
      const next = sayiNormalizeEt(action.payload);
      state.bm4.selectedMahalleId = next ?? null;
    },

    dashboardV2Bm4HaritaGorunumuAyarla(state, action) {
      const payload = action.payload || {};
      const nextCenter = latLngNormalizeEt(payload?.center || payload);
      const nextZoom = zoomNormalizeEt(payload?.zoom ?? payload?.zoom_level);

      state.bm4.mapView = {
        center: nextCenter || state.bm4.mapView.center,
        zoom: nextZoom !== null ? nextZoom : state.bm4.mapView.zoom,
        source: payload?.source || "user",
      };
    },

    dashboardV2Bm4VeriyiTemizle(state) {
      temizleBm4State(state);
    },

    dashboardV2ExpandWidgetAyarla(state, action) {
      const next = widgetKeyNormalizeEt(action.payload) || null;
      state.expandAcikWidget = next;
    },

    dashboardV2WidgetDetayTemizle(state, action) {
      const key = widgetKeyNormalizeEt(action.payload);
      if (!key) return;

      delete state.widgetDetaylari[key];
      delete state.widgetYukleniyorByKey[key];
      delete state.widgetHataByKey[key];
    },

    dashboardV2TumWidgetDetaylariniTemizle(state) {
      temizleWidgetDetayState(state);
    },

    dashboardV2HataTemizle(state) {
      log("ACTION dashboardV2HataTemizle", { before: state.hata });
      state.hata = null;
      state.bm4.hata = null;
    },

    dashboardV2VeriyiTemizle(state) {
      state.uygulananFiltreler = null;
      state.meta = null;
      state.kpi = null;
      state.grafikler = null;
      state.harita = null;
      state.kirilimlar = null;
      state.sonYeniBasvurular = null;
      state.cevapsizBasvurular = null;

      temizleBm4State(state);

      state.expandAcikWidget = null;
      temizleWidgetDetayState(state);

      state.hata = null;
      state.sonGuncellemeEpochMs = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(dashboardV2SorguGetir.pending, (state, action) => {
        state.yukleniyor = true;
        state.hata = null;

        state.sonIstekParametreleri = action?.meta?.arg || null;
        state.sonIstekRequestId = action?.meta?.requestId || null;
        state.sonIstekZamanlari = { pendingAt: nowIso() };

        log("PENDING", {
          requestId: state.sonIstekRequestId,
          arg: state.sonIstekParametreleri,
          filtreler: debugSummarizeFilters(state.filtreler),
        });
      })
      .addCase(dashboardV2SorguGetir.fulfilled, (state, action) => {
        state.yukleniyor = false;

        const payload = action.payload?.data || {};
        state.uygulananFiltreler = payload.uygulananFiltreler || null;
        state.meta = payload.meta || null;
        state.kpi = payload.kpi || null;
        state.grafikler = payload.grafikler || null;
        state.harita = payload.harita || null;
        state.kirilimlar = payload.kirilimlar || null;
        state.sonYeniBasvurular = payload.sonYeniBasvurular || [];
        state.cevapsizBasvurular = payload.cevapsizBasvurular || [];

        state.sonGuncellemeEpochMs = Date.now();
        state.sonIstekParametreleri =
          action.payload?.uygulananParametreler || null;
        state.sonIstekRequestId =
          action.payload?.requestId || state.sonIstekRequestId;

        state.sonIstekZamanlari = {
          ...(state.sonIstekZamanlari || {}),
          fulfilledAt: nowIso(),
          startedAt: action.payload?.startedAt,
          finishedAt: action.payload?.finishedAt,
        };

        log("FULFILLED", {
          requestId: state.sonIstekRequestId,
          uygulananParametreler: state.sonIstekParametreleri,
          meta: state.meta,
          applied: state.uygulananFiltreler,
        });

        if (
          state.meta?.baskanMi &&
          state.meta?.birimModu &&
          state.meta.birimModu !== "tümü"
        ) {
          warn("FULFILLED: Başkan ama birimModu tümü değil:", {
            birimModu: state.meta.birimModu,
            aktifBirimId: state.meta.aktifBirimId,
            izinliBirimCount: Array.isArray(state.meta.izinliBirimIdleri)
              ? state.meta.izinliBirimIdleri.length
              : null,
            sonIstekParametreleri: state.sonIstekParametreleri,
          });
        }
      })
      .addCase(dashboardV2SorguGetir.rejected, (state, action) => {
        state.yukleniyor = false;

        state.hata = action.payload || {
          mesaj: action.error?.message || "Dashboard verisi alınamadı.",
        };

        state.sonIstekZamanlari = {
          ...(state.sonIstekZamanlari || {}),
          rejectedAt: nowIso(),
        };

        errlog("REJECTED", {
          requestId: state.sonIstekRequestId,
          hata: state.hata,
        });
      })

      .addCase(dashboardV2SessizSorguGetir.pending, (state) => {
        state.sessizYukleniyor = true;
      })
      .addCase(dashboardV2SessizSorguGetir.fulfilled, (state, action) => {
        state.sessizYukleniyor = false;

        const payload = action.payload?.data || {};
        state.uygulananFiltreler = payload.uygulananFiltreler || null;
        state.meta = payload.meta || null;
        state.kpi = payload.kpi || null;
        state.grafikler = payload.grafikler || null;
        state.harita = payload.harita || null;
        state.kirilimlar = payload.kirilimlar || null;
        state.sonYeniBasvurular = payload.sonYeniBasvurular || [];
        state.cevapsizBasvurular = payload.cevapsizBasvurular || [];

        state.sonGuncellemeEpochMs = Date.now();
        state.sonIstekParametreleri =
          action.payload?.uygulananParametreler || null;
        state.sonIstekRequestId =
          action.payload?.requestId || state.sonIstekRequestId;

        state.sonIstekZamanlari = {
          ...(state.sonIstekZamanlari || {}),
          silentFulfilledAt: nowIso(),
        };
      })
      .addCase(dashboardV2SessizSorguGetir.rejected, (state, action) => {
        state.sessizYukleniyor = false;
        errlog("SILENT REJECTED", action.payload || action.error);
      })

      .addCase(dashboardV2WidgetDetayGetir.pending, (state, action) => {
        const widget = widgetKeyNormalizeEt(action.meta?.arg?.widget);
        if (!widget) return;

        state.widgetYukleniyorByKey[widget] = true;
        delete state.widgetHataByKey[widget];
      })
      .addCase(dashboardV2WidgetDetayGetir.fulfilled, (state, action) => {
        const widget = widgetKeyNormalizeEt(action.payload?.widget);
        if (!widget) return;

        state.widgetYukleniyorByKey[widget] = false;
        delete state.widgetHataByKey[widget];

        state.widgetDetaylari[widget] = {
          meta: action.payload?.data?.meta || null,
          uygulananFiltreler: action.payload?.data?.uygulananFiltreler || null,
          widgetData: action.payload?.data?.widgetData || null,
          uygulananParametreler: action.payload?.uygulananParametreler || null,
          loadedAt: Date.now(),
          requestId: action.payload?.requestId || null,
        };
      })
      .addCase(dashboardV2WidgetDetayGetir.rejected, (state, action) => {
        const widget = widgetKeyNormalizeEt(
          action.payload?.widget || action.meta?.arg?.widget
        );
        if (!widget) return;

        state.widgetYukleniyorByKey[widget] = false;
        state.widgetHataByKey[widget] = action.payload || {
          mesaj: action.error?.message || "Widget detayı alınamadı.",
        };
      })

      /* --------------------------------------------------------------------
         BM4 reducers
      -------------------------------------------------------------------- */

      .addCase(dashboardV2Bm4DefaultConfigGetir.pending, (state) => {
        state.bm4.hata = null;
        state.bm4.defaultConfigMeta = {
          ...(state.bm4.defaultConfigMeta || {}),
          yukleniyor: true,
        };
      })
      .addCase(dashboardV2Bm4DefaultConfigGetir.fulfilled, (state, action) => {
        const payload = action.payload?.data || {};
        const defaultConfig = payload?.default || null;
        const view = bm4ViewFromPayload(payload);

        state.bm4.defaultConfig = defaultConfig;
        state.bm4.defaultConfigMeta = {
          isFallback: Boolean(payload?.is_fallback),
          loadedAt: Date.now(),
          requestId: action.payload?.requestId || null,
          yukleniyor: false,
        };

        if (!state.bm4.selectedLayerLevel && defaultConfig?.level) {
          state.bm4.selectedLayerLevel =
            bm4LevelNormalizeEt(defaultConfig.level) || null;
        }

        if (state.bm4.selectedBoundaryId == null && defaultConfig?.id != null) {
          state.bm4.selectedBoundaryId = sayiNormalizeEt(defaultConfig.id) ?? null;
        }

        if (
          (state.bm4.mapView.center == null && view.center) ||
          (state.bm4.mapView.zoom == null && view.zoom !== null)
        ) {
          state.bm4.mapView = {
            center: view.center || state.bm4.mapView.center,
            zoom: view.zoom !== null ? view.zoom : state.bm4.mapView.zoom,
            source: "default",
          };
        }
      })
      .addCase(dashboardV2Bm4DefaultConfigGetir.rejected, (state, action) => {
        state.bm4.defaultConfigMeta = {
          ...(state.bm4.defaultConfigMeta || {}),
          yukleniyor: false,
        };
        state.bm4.hata = action.payload || {
          mesaj: action.error?.message || "BM4 varsayılan config alınamadı.",
        };
      })

      .addCase(dashboardV2Bm4BoundaryListGetir.pending, (state, action) => {
        const level = bm4LevelNormalizeEt(action.meta?.arg?.level ?? action.meta?.arg);
        if (!level) return;

        state.bm4.boundaryYukleniyorByLevel[level] = true;
        state.bm4.hata = null;
      })
      .addCase(dashboardV2Bm4BoundaryListGetir.fulfilled, (state, action) => {
        const level = bm4LevelNormalizeEt(action.payload?.level);
        if (!level) return;

        const payload = action.payload?.data || {};

        state.bm4.boundaryYukleniyorByLevel[level] = false;
        state.bm4.boundaryListByLevel[level] = {
          count: payload?.count ?? 0,
          results: payload?.results || [],
          appliedFilters: payload?.applied_filters || null,
          loadedAt: Date.now(),
          requestId: action.payload?.requestId || null,
        };

        if (!state.bm4.selectedLayerLevel) {
          state.bm4.selectedLayerLevel = level;
        }
      })
      .addCase(dashboardV2Bm4BoundaryListGetir.rejected, (state, action) => {
        const level = bm4LevelNormalizeEt(
          action.payload?.level ?? action.meta?.arg?.level ?? action.meta?.arg
        );
        if (level) {
          state.bm4.boundaryYukleniyorByLevel[level] = false;
        }

        state.bm4.hata = action.payload || {
          mesaj: action.error?.message || "BM4 boundary listesi alınamadı.",
        };
      })

      .addCase(dashboardV2Bm4BoundaryDetailGetir.pending, (state, action) => {
        const boundaryId = sayiNormalizeEt(
          action.meta?.arg?.boundaryId ?? action.meta?.arg?.id ?? action.meta?.arg
        );
        if (!boundaryId) return;

        state.bm4.boundaryDetailYukleniyorById[boundaryId] = true;
        state.bm4.hata = null;
      })
      .addCase(dashboardV2Bm4BoundaryDetailGetir.fulfilled, (state, action) => {
        const boundaryId = sayiNormalizeEt(action.payload?.boundaryId);
        if (!boundaryId) return;

        const payload = action.payload?.data || {};
        const view = bm4ViewFromPayload(payload);

        state.bm4.boundaryDetailYukleniyorById[boundaryId] = false;
        state.bm4.boundaryDetailById[boundaryId] = {
          data: payload,
          loadedAt: Date.now(),
          requestId: action.payload?.requestId || null,
        };

        if (state.bm4.selectedBoundaryId === boundaryId) {
          if (payload?.level) {
            state.bm4.selectedLayerLevel =
              bm4LevelNormalizeEt(payload.level) || state.bm4.selectedLayerLevel;
          }

          if (payload?.neighborhood_id || payload?.neighborhood?.id) {
            state.bm4.selectedMahalleId =
              sayiNormalizeEt(payload?.neighborhood_id ?? payload?.neighborhood?.id) ??
              state.bm4.selectedMahalleId;
          }

          if (view.center || view.zoom !== null) {
            state.bm4.mapView = {
              center: view.center || state.bm4.mapView.center,
              zoom: view.zoom !== null ? view.zoom : state.bm4.mapView.zoom,
              source: "boundary",
            };
          }
        }
      })
      .addCase(dashboardV2Bm4BoundaryDetailGetir.rejected, (state, action) => {
        const boundaryId = sayiNormalizeEt(
          action.payload?.boundaryId ??
            action.meta?.arg?.boundaryId ??
            action.meta?.arg?.id ??
            action.meta?.arg
        );

        if (boundaryId) {
          state.bm4.boundaryDetailYukleniyorById[boundaryId] = false;
        }

        state.bm4.hata = action.payload || {
          mesaj: action.error?.message || "BM4 boundary detayı alınamadı.",
        };
      })

      .addCase(dashboardV2Bm4MahalleStatsGetir.pending, (state) => {
        state.bm4.mahalleStatsYukleniyor = true;
        state.bm4.hata = null;
      })
      .addCase(dashboardV2Bm4MahalleStatsGetir.fulfilled, (state, action) => {
        const payload = action.payload?.data || {};

        state.bm4.mahalleStatsYukleniyor = false;
        state.bm4.mahalleStats = {
          count: payload?.count ?? 0,
          results: payload?.results || [],
          selectedBoundary: payload?.selected_boundary || null,
          appliedFilters: payload?.applied_filters || null,
          loadedAt: Date.now(),
          requestId: action.payload?.requestId || null,
          uygulananParametreler: action.payload?.uygulananParametreler || null,
        };
      })
      .addCase(dashboardV2Bm4MahalleStatsGetir.rejected, (state, action) => {
        state.bm4.mahalleStatsYukleniyor = false;
        state.bm4.hata = action.payload || {
          mesaj: action.error?.message || "BM4 mahalle istatistikleri alınamadı.",
        };
      });
  },
});

export const {
  dashboardV2FiltreleriSifirla,
  dashboardV2FiltreleriAyarla,
  dashboardV2TarihAyarla,
  dashboardV2BirimAyarla,
  dashboardV2DurumlarAyarla,
  dashboardV2HaritaLimitAyarla,

  dashboardV2Bm4KatmanAyarla,
  dashboardV2Bm4SeciliBoundaryAyarla,
  dashboardV2Bm4SeciliBoundaryTemizle,
  dashboardV2Bm4SeciliMahalleAyarla,
  dashboardV2Bm4HaritaGorunumuAyarla,
  dashboardV2Bm4VeriyiTemizle,

  dashboardV2ExpandWidgetAyarla,
  dashboardV2WidgetDetayTemizle,
  dashboardV2TumWidgetDetaylariniTemizle,
  dashboardV2HataTemizle,
  dashboardV2VeriyiTemizle,
} = belediyeDashboardV2Slice.actions;

export default belediyeDashboardV2Slice.reducer;

/* ----------------------------------------------------------------------------
   Selectors
---------------------------------------------------------------------------- */

export const selectDashboardV2 = (state) => state?.belediyeDashboardV2;
export const selectDashboardV2Filtreler = (state) =>
  state?.belediyeDashboardV2?.filtreler;
export const selectDashboardV2Meta = (state) =>
  state?.belediyeDashboardV2?.meta;
export const selectDashboardV2UygulananFiltreler = (state) =>
  state?.belediyeDashboardV2?.uygulananFiltreler;

export const selectDashboardV2Kpi = (state) =>
  state?.belediyeDashboardV2?.kpi;
export const selectDashboardV2Grafikler = (state) =>
  state?.belediyeDashboardV2?.grafikler;
export const selectDashboardV2Harita = (state) =>
  state?.belediyeDashboardV2?.harita;
export const selectDashboardV2Kirilimlar = (state) =>
  state?.belediyeDashboardV2?.kirilimlar;
export const selectDashboardV2SonYeniBasvurular = (state) =>
  state?.belediyeDashboardV2?.sonYeniBasvurular;
export const selectDashboardV2CevapsizBasvurular = (state) =>
  state?.belediyeDashboardV2?.cevapsizBasvurular;

export const selectDashboardV2Yukleniyor = (state) =>
  state?.belediyeDashboardV2?.yukleniyor;
export const selectDashboardV2SessizYukleniyor = (state) =>
  state?.belediyeDashboardV2?.sessizYukleniyor;
export const selectDashboardV2Hata = (state) =>
  state?.belediyeDashboardV2?.hata;
export const selectDashboardV2SonGuncellemeEpochMs = (state) =>
  state?.belediyeDashboardV2?.sonGuncellemeEpochMs;

export const selectDashboardV2ExpandAcikWidget = (state) =>
  state?.belediyeDashboardV2?.expandAcikWidget;
export const selectDashboardV2WidgetDetaylari = (state) =>
  state?.belediyeDashboardV2?.widgetDetaylari || {};
export const selectDashboardV2WidgetYukleniyorByKey = (state) =>
  state?.belediyeDashboardV2?.widgetYukleniyorByKey || {};
export const selectDashboardV2WidgetHataByKey = (state) =>
  state?.belediyeDashboardV2?.widgetHataByKey || {};

export const selectDashboardV2WidgetDetayByKey = (state, key) =>
  state?.belediyeDashboardV2?.widgetDetaylari?.[key] || null;

export const selectDashboardV2WidgetYukleniyor = (state, key) =>
  Boolean(state?.belediyeDashboardV2?.widgetYukleniyorByKey?.[key]);

export const selectDashboardV2WidgetHata = (state, key) =>
  state?.belediyeDashboardV2?.widgetHataByKey?.[key] || null;

/* ----------------------------------------------------------------------------
   Selectors — BM4
---------------------------------------------------------------------------- */

export const selectDashboardV2Bm4 = (state) =>
  state?.belediyeDashboardV2?.bm4 || ilkBm4State();

export const selectDashboardV2Bm4Hata = (state) =>
  state?.belediyeDashboardV2?.bm4?.hata || null;

export const selectDashboardV2Bm4DefaultConfig = (state) =>
  state?.belediyeDashboardV2?.bm4?.defaultConfig || null;

export const selectDashboardV2Bm4DefaultConfigMeta = (state) =>
  state?.belediyeDashboardV2?.bm4?.defaultConfigMeta || null;

export const selectDashboardV2Bm4BoundaryListByLevel = (state) =>
  state?.belediyeDashboardV2?.bm4?.boundaryListByLevel || ilkBm4BoundaryListByLevel();

export const selectDashboardV2Bm4BoundariesForLevel = (state, level) => {
  const normalizedLevel = bm4LevelNormalizeEt(level);
  if (!normalizedLevel) return [];
  return (
    state?.belediyeDashboardV2?.bm4?.boundaryListByLevel?.[normalizedLevel]?.results ||
    []
  );
};

export const selectDashboardV2Bm4BoundaryYukleniyorByLevel = (state) =>
  state?.belediyeDashboardV2?.bm4?.boundaryYukleniyorByLevel || {};

export const selectDashboardV2Bm4BoundaryLevelYukleniyor = (state, level) => {
  const normalizedLevel = bm4LevelNormalizeEt(level);
  if (!normalizedLevel) return false;
  return Boolean(
    state?.belediyeDashboardV2?.bm4?.boundaryYukleniyorByLevel?.[normalizedLevel]
  );
};

export const selectDashboardV2Bm4BoundaryDetailById = (state) =>
  state?.belediyeDashboardV2?.bm4?.boundaryDetailById || {};

export const selectDashboardV2Bm4BoundaryDetail = (state, boundaryId) => {
  const id = sayiNormalizeEt(boundaryId);
  if (!id) return null;
  return state?.belediyeDashboardV2?.bm4?.boundaryDetailById?.[id]?.data || null;
};

export const selectDashboardV2Bm4BoundaryDetailYukleniyorById = (state) =>
  state?.belediyeDashboardV2?.bm4?.boundaryDetailYukleniyorById || {};

export const selectDashboardV2Bm4BoundaryDetailYukleniyor = (state, boundaryId) => {
  const id = sayiNormalizeEt(boundaryId);
  if (!id) return false;
  return Boolean(
    state?.belediyeDashboardV2?.bm4?.boundaryDetailYukleniyorById?.[id]
  );
};

export const selectDashboardV2Bm4MahalleStatsEnvelope = (state) =>
  state?.belediyeDashboardV2?.bm4?.mahalleStats || null;

export const selectDashboardV2Bm4MahalleStats = (state) =>
  state?.belediyeDashboardV2?.bm4?.mahalleStats?.results || [];

export const selectDashboardV2Bm4MahalleStatsYukleniyor = (state) =>
  Boolean(state?.belediyeDashboardV2?.bm4?.mahalleStatsYukleniyor);

export const selectDashboardV2Bm4SelectedLayerLevel = (state) =>
  state?.belediyeDashboardV2?.bm4?.selectedLayerLevel || null;

export const selectDashboardV2Bm4SelectedBoundaryId = (state) =>
  state?.belediyeDashboardV2?.bm4?.selectedBoundaryId || null;

export const selectDashboardV2Bm4SelectedMahalleId = (state) =>
  state?.belediyeDashboardV2?.bm4?.selectedMahalleId || null;

export const selectDashboardV2Bm4MapView = (state) =>
  state?.belediyeDashboardV2?.bm4?.mapView || {
    center: null,
    zoom: null,
    source: null,
  };

export const selectDashboardV2Bm4HerhangiBirYukleniyor = (state) => {
  const bm4 = state?.belediyeDashboardV2?.bm4;
  if (!bm4) return false;

  const anyBoundaryLevelLoading = Object.values(
    bm4.boundaryYukleniyorByLevel || {}
  ).some(Boolean);

  const anyBoundaryDetailLoading = Object.values(
    bm4.boundaryDetailYukleniyorById || {}
  ).some(Boolean);

  const defaultLoading = Boolean(bm4.defaultConfigMeta?.yukleniyor);
  const mahalleStatsLoading = Boolean(bm4.mahalleStatsYukleniyor);

  return (
    defaultLoading ||
    mahalleStatsLoading ||
    anyBoundaryLevelLoading ||
    anyBoundaryDetailLoading
  );
};